/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { Skeleton } from "primereact/skeleton";
import { Menu } from "primereact/menu";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import {
  Boton,
  Titulo,
  Formulario,
  accessAPI,
  Sidebar,
  tituloBase,
  TablaFiltrable
} from "../../../shared";

// Las categorías no tienen ID: los archivos guardan el nombre como texto
// plano, así que editar o eliminar una categoría no afecta archivos
// existentes. Por eso cada cambio reemplaza la lista completa en la API.
export default function CategoriasArchivos() {
  const [loader, setLoader] = useState(true);
  const [categorias, setCategorias] = useState([]);
  const [sideBar, setSideBar] = useState(false);
  const [formLoader, setFormLoader] = useState(false);
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState(null);
  const [formKey, setFormKey] = useState(0);

  const toast = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    document.title = "Categorías de archivos";
    accessAPI(
      "GET",
      "admin/config/categoriasarchivos",
      null,
      (respuesta) => {
        setCategorias(respuesta);
        setLoader(false);
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al cargar categorías",
          sticky: true
        });
        setLoader(false);
      }
    );
    return () => (document.title = tituloBase());
  }, []);

  // Guarda la lista completa en la API y actualiza el state con la respuesta
  function guardarCategorias(nuevasCategorias, alTerminar) {
    setFormLoader(true);
    accessAPI(
      "PUT",
      "admin/config/categoriasarchivos",
      { categorias: nuevasCategorias },
      (respuesta) => {
        setCategorias(respuesta);
        setFormLoader(false);
        if (alTerminar) alTerminar();
      },
      (respuesta) => {
        setFormLoader(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al guardar categorías",
          sticky: true
        });
      }
    );
  }

  function crearOEditarCategoria(contenidoCampos) {
    const nueva = {
      nombre: contenidoCampos.nombre,
      disponibleColaboradores: contenidoCampos.disponibleColaboradores === true
    };

    const nuevasCategorias = categoriaSeleccionada
      ? categorias.map((c) =>
          c.nombre === categoriaSeleccionada.nombre ? nueva : c
        )
      : [...categorias, nueva];

    guardarCategorias(nuevasCategorias, () => {
      setSideBar(false);
      toast.current?.show({
        severity: "success",
        summary: "Éxito",
        detail: categoriaSeleccionada
          ? "Categoría actualizada correctamente"
          : "Categoría creada correctamente",
        life: 3000
      });
    });
  }

  function eliminarCategoria(categoria) {
    guardarCategorias(
      categorias.filter((c) => c.nombre !== categoria.nombre),
      () => {
        toast.current?.show({
          severity: "success",
          summary: "Éxito",
          detail: "Categoría eliminada correctamente",
          life: 3000
        });
      }
    );
  }

  const menuItems = [
    {
      label: "Editar",
      icon: "pi pi-pencil",
      command: () => {
        setFormKey((k) => k + 1);
        setSideBar(true);
      }
    },
    {
      label: "Eliminar",
      icon: "pi pi-trash",
      className: "p-menuitem-danger",
      command: () => {
        confirmDialog({
          message:
            "¿Está seguro que desea eliminar esta categoría? Los archivos ya categorizados no se verán afectados.",
          header: "Confirmar eliminación",
          icon: "pi pi-exclamation-triangle",
          acceptLabel: "Eliminar",
          rejectLabel: "Cancelar",
          acceptClassName: "p-button-danger",
          accept: () => eliminarCategoria(categoriaSeleccionada)
        });
      }
    }
  ];

  const formConfig = {
    secciones: [
      {
        campos: [
          {
            nombre: "nombre",
            tipoitemformularioid: 1,
            etiqueta: "Nombre",
            requerido: true,
            respuesta: categoriaSeleccionada?.nombre
          },
          {
            nombre: "disponibleColaboradores",
            tipoitemformularioid: 6,
            etiqueta: "Disponible para colaboradores",
            respuesta: categoriaSeleccionada?.disponibleColaboradores === true
          }
        ]
      }
    ],
    botones: [
      {
        etiqueta: categoriaSeleccionada ? "Guardar" : "Crear categoría",
        callback: crearOEditarCategoria,
        validar: true
      }
    ]
  };

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <ConfirmDialog />
      <Menu model={menuItems} popup ref={menuRef} />

      <Sidebar
        visible={sideBar}
        onHide={() => setSideBar(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>
          {categoriaSeleccionada ? "Editar categoría" : "Nueva categoría"}
        </Titulo>
        <Formulario key={formKey} config={formConfig} loader={formLoader} />
      </Sidebar>

      {loader && (
        <div
          css={{
            marginTop: "2em",
            display: "flex",
            flexDirection: "column",
            gap: "0.5rem"
          }}
        >
          <div
            css={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "0.5rem"
            }}
          >
            <Skeleton width="15rem" height="2.5rem" />
            <Skeleton width="12rem" height="2.5rem" />
          </div>
          <Card>
            <div
              css={{
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem"
              }}
            >
              <Skeleton width="100%" height="3rem" />
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} width="100%" height="3.5rem" />
              ))}
            </div>
          </Card>
        </div>
      )}

      {!loader && (
        <div>
          <Titulo
            urlVolver="/config"
            botones={
              <Boton
                callback={() => {
                  setCategoriaSeleccionada(null);
                  setFormKey((k) => k + 1);
                  setSideBar(true);
                }}
              >
                Nueva categoría
              </Boton>
            }
          >
            Categorías de archivos
          </Titulo>

          <Card>
            <TablaFiltrable conFiltros={false}
              value={categorias}
              nombreArchivo="categorias_archivos"
              emptyMessage="No hay categorías de archivos"
              stripedRows
              size="small"
            >
              <Column header="Nombre" field="nombre" />
              <Column
                header="Disponible para colaboradores"
                body={(rowData) =>
                  rowData.disponibleColaboradores ? (
                    <i className="pi pi-check" css={{ color: "var(--verde)" }} />
                  ) : null
                }
                style={{ width: "16rem", textAlign: "center" }}
              />
              <Column
                body={(rowData) => (
                  <Boton
                    icono
                    chico
                    negro
                    callback={(e) => {
                      setCategoriaSeleccionada(rowData);
                      menuRef.current.toggle(e);
                    }}
                  >
                    <i className="pi pi-ellipsis-v"></i>
                  </Boton>
                )}
                style={{ width: "3em", textAlign: "center" }}
              />
            </TablaFiltrable>
          </Card>
        </div>
      )}
    </>
  );
}
